import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const formatMoney = (value) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value || 0);

function Finances() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [operatorSummary, setOperatorSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([API.get("/credits"), API.get("/payments"), API.get("/operator-payments/summary")])
      .then(([creditsRes, paymentsRes, operatorSummaryRes]) => { setCredits(creditsRes.data); setPayments(paymentsRes.data); setOperatorSummary(operatorSummaryRes.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const totalDebt = credits.filter((credit) => credit.activo === true && credit.estado !== "PAGADO" && credit.estado !== "REFINANCIADO").reduce((sum, credit) => sum + (credit.saldoPendiente || 0), 0);
  const capitalLoaned = credits.filter((credit) => !credit.creditoOrigen).reduce((sum, credit) => sum + (credit.capital || 0), 0);
  const totalCollected = payments.reduce((sum, payment) => sum + (payment.monto || 0), 0);
  const pendingCommissions = operatorSummary.reduce((sum, item) => sum + (item.pendiente || 0), 0);
  const paidCommissions = operatorSummary.reduce((sum, item) => sum + (item.totalPagado || 0), 0);
  const recentPayments = useMemo(() => [...payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 8), [payments]);
  const chartData = [...recentPayments].reverse().map((payment, index) => ({ name: `Pago ${index + 1}`, monto: payment.monto }));
  const overdueCredits = credits.filter((credit) => credit.estado === "MOROSO" && credit.activo === true);

  if (loading) return <PageHeader title="Finanzas" subtitle="Cargando información financiera..." />;

  return (
    <div className="cc-finances">
      <PageHeader title="Finanzas" subtitle="Sector privado con los importes y movimientos del negocio" />
      <div className="cc-finance-notice">Esta sección concentra la información monetaria para que no aparezca en la pantalla inicial.</div>
      <div className="cc-kpi-grid">
        {[['Deuda total', totalDebt], ['Capital prestado', capitalLoaned], ['Cobrado', totalCollected], ['Comisiones pendientes', pendingCommissions], ['Comisiones pagadas', paidCommissions]].map(([label, value]) => <Card key={label}><h3>{label}</h3><p className="cc-dashboard-number">{formatMoney(value)}</p></Card>)}
      </div>
      <Card><h2>Evolución de cobranzas</h2><div style={{ width: "100%", height: 300 }}><ResponsiveContainer><LineChart data={chartData}><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(value) => formatMoney(value)} /><Line type="monotone" dataKey="monto" stroke="#f97316" strokeWidth={3} /></LineChart></ResponsiveContainer></div></Card>
      <div className="cc-finance-columns">
        <Card><h2>Pagos recientes</h2>{recentPayments.length === 0 ? <p>No hay pagos registrados todavía.</p> : recentPayments.map((payment) => <div className="cc-finance-row" key={payment._id}><strong>{formatMoney(payment.monto)}</strong><span>{new Date(payment.createdAt).toLocaleString()}</span></div>)}</Card>
        <Card><h2>Créditos morosos</h2>{overdueCredits.length === 0 ? <p className="cc-number-green">No hay créditos morosos.</p> : overdueCredits.map((credit) => <button className="cc-finance-row cc-finance-link" key={credit._id} onClick={() => navigate(`/client/${credit.cliente?._id}`)}><strong>{credit.cliente?.nombre || "Cliente"}</strong><span>{formatMoney(credit.saldoPendiente)}</span></button>)}</Card>
      </div>
    </div>
  );
}

export default Finances;
